import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { Crown, Check, Zap, Sparkles } from "lucide-react";

const TIER_META = {
  silver:   { color: "#c0c0c0", icon: "★",  glow: "rgba(192,192,192,0.3)" },
  gold:     { color: "#d4af37", icon: "★★", glow: "rgba(212,175,55,0.4)" },
  platinum: { color: "#b9f2ff", icon: "★★★", glow: "rgba(185,242,255,0.4)" },
};

export default function Subscribe() {
  const { user, currency } = useApp();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(null);
  const [interval, setInterval] = useState("month");
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/subscriptions/plans").then(r => setPlans(r.data)).catch(() => {});
    api.get("/subscriptions/me").then(r => setMySub(r.data)).catch(() => {});
    const sid = params.get("session_id");
    if (sid) {
      api.get(`/subscriptions/status/${sid}`).then(r => {
        if (r.data.active) {
          toast.success(`Havana Plus ${r.data.tier.toUpperCase()} activated!`);
          api.get("/subscriptions/me").then(r2 => setMySub(r2.data));
        }
      }).catch(() => {});
    }
  }, [params]);

  const subscribe = async (planId) => {
    setLoading(planId);
    try {
      const r = await api.post("/subscriptions/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
      setLoading(null);
    }
  };

  const switchPlan = async (planId) => {
    if (!window.confirm("Switch to this plan? The change is prorated by Stripe — you'll be charged or credited the difference for the current period.")) return;
    setLoading(planId);
    try {
      const r = await api.post("/subscriptions/switch", { plan_id: planId, origin_url: window.location.origin });
      toast.success(`Switched to ${r.data.tier.toUpperCase()}`);
      api.get("/subscriptions/me").then(rr => setMySub(rr.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setLoading(null); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription at the end of the current period? You'll keep VIP perks until then.")) return;
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Subscription will cancel at period end");
      api.get("/subscriptions/me").then(r => setMySub(r.data));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const cur = currency === "EUR" ? "eur" : "huf";
  const tierPlans = ["silver", "gold", "platinum"].map(t => plans.find(p => p.tier === t && p.currency === cur && p.interval === interval)).filter(Boolean);
  const monthlyOf = (tier) => plans.find(p => p.tier === tier && p.currency === cur && p.interval === "month");
  const fmtPrice = (p) => p.currency === "eur" ? `€${(p.amount/100).toFixed(2)}` : `${(p.amount/100).toLocaleString("hu-HU")} Ft`;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
      <div className="text-center mb-12">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#d4af37] mb-3">Havana Plus</div>
        <h1 className="font-serif text-4xl lg:text-5xl gold-text flex items-center justify-center gap-3"><Crown size={36}/>VIP Membership</h1>
        <p className="text-white/50 mt-4 max-w-xl mx-auto text-sm">Választható havi előfizetés napi GC bónusszal, XP-szorzóval és exclusive VIP tournamenttel. Bármikor lemondható. {currency === "EUR" ? "EUR billing." : "HUF billing."}</p>
        <div className="inline-flex items-center gap-1 p-1 mt-6 bg-black/40 border border-white/10 rounded-full" data-testid="interval-toggle">
          <button data-testid="toggle-month" onClick={() => setInterval("month")} className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest transition ${interval==="month" ? "btn-gold" : "text-white/60"}`}>Monthly</button>
          <button data-testid="toggle-year" onClick={() => setInterval("year")} className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest transition relative ${interval==="year" ? "btn-gold" : "text-white/60"}`}>
            Annual
            <span className="absolute -top-2 -right-2 bg-emerald-400 text-black text-[9px] px-1.5 py-0.5 rounded-full font-bold">-15%</span>
          </button>
        </div>
      </div>

      {mySub?.active && (
        <div className="lux-card p-6 mb-10 border-[#d4af37]/60" data-testid="active-sub-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Active subscription</div>
              <div className="font-serif text-xl mt-1" style={{color: TIER_META[mySub.tier]?.color}}>
                Havana Plus · {mySub.tier?.toUpperCase()}
              </div>
              {mySub.cancel_at_period_end && <div className="text-xs text-yellow-400 mt-1">Cancels at period end</div>}
            </div>
            {!mySub.cancel_at_period_end && (
              <button data-testid="cancel-sub" onClick={cancel} className="btn-ghost px-5 py-2.5 rounded-md text-xs">Cancel subscription</button>
            )}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {tierPlans.map(p => {
          const meta = TIER_META[p.tier];
          const isCurrent = mySub?.active && mySub.tier === p.tier;
          const featured = p.tier === "gold";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
                 className={`lux-card p-7 text-center relative ${featured ? "scale-105 border-[#d4af37]/60" : ""}`}
                 style={featured ? {boxShadow: `0 0 40px ${meta.glow}`} : {}}>
              {featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d4af37] text-black text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">Most popular</div>}
              <div className="text-3xl mb-2" style={{color: meta.color}}>{meta.icon}</div>
              <div className="font-serif text-2xl mb-1" style={{color: meta.color}}>{p.tier.toUpperCase()}</div>
              <div className="font-serif text-4xl gold-text mt-4">{fmtPrice(p)}</div>
              <div className="text-xs text-white/40 mt-1">/ {interval === "year" ? "év" : "hó"}</div>
              {interval === "year" && monthlyOf(p.tier) && (
                <div className="text-[10px] text-emerald-400 mt-1">Save {Math.round((1 - p.amount / (monthlyOf(p.tier).amount * 12)) * 100)}% vs monthly</div>
              )}
              <ul className="text-sm text-left mt-6 space-y-2.5 mb-7">
                <li className="flex items-start gap-2"><Check className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span><strong className="text-[#d4af37]">{p.daily_gc.toLocaleString()} GC</strong> napi bónusz</span></li>
                <li className="flex items-start gap-2"><Zap className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span>{p.tier === "platinum" ? "2.0×" : p.tier === "gold" ? "1.5×" : "1.2×"} XP multiplier</span></li>
                <li className="flex items-start gap-2"><Sparkles className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span>{p.tier.toUpperCase()} VIP badge</span></li>
                <li className="flex items-start gap-2"><Check className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span>Exclusive heti tournament</span></li>
                {p.tier !== "silver" && <li className="flex items-start gap-2"><Check className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span>Reklámmentes mód</span></li>}
                {p.tier === "platinum" && <li className="flex items-start gap-2"><Check className="text-emerald-400 mt-0.5 shrink-0" size={16}/><span>Priority support · Personal account manager</span></li>}
              </ul>
              {isCurrent ? (
                <button disabled className="btn-ghost w-full py-3 rounded-md text-sm cursor-default">Current plan</button>
              ) : mySub?.active ? (
                <button data-testid={`switch-${p.id}`} onClick={() => switchPlan(p.id)} disabled={loading === p.id}
                        className={featured ? "btn-gold w-full py-3 rounded-md text-sm" : "btn-ghost w-full py-3 rounded-md text-sm"}>
                  {loading === p.id ? "…" : "Switch plan"}
                </button>
              ) : (
                <button data-testid={`subscribe-${p.id}`} onClick={() => subscribe(p.id)} disabled={loading === p.id}
                        className={featured ? "btn-gold w-full py-3 rounded-md text-sm" : "btn-ghost w-full py-3 rounded-md text-sm"}>
                  {loading === p.id ? "…" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center text-xs text-white/40">
        Stripe-on át recurring billing · Automatikus megújulás · Bármikor lemondható
      </div>
    </div>
  );
}
